/**
 * API Client
 * Centralized HTTP client with token management, error handling, and retry logic
 */

import TokenManager from "./token-manager";
import { ErrorHandler } from "./error-handler";
import { ApiError, RequestConfig } from "../../types/api";

class ApiClient {
  private static instance: ApiClient;
  private baseUrl: string;
  private timeout: number = 30000; // 30 seconds default
  private maxRetries: number = 2;

  private constructor() {
    this.baseUrl =
      import.meta.env.VITE_API_URL || "https://api.trailblazerops.com";
  }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Make HTTP request with automatic token injection and error handling
   */
  async request<T = any>(
    endpoint: string,
    config: RequestConfig = {},
  ): Promise<T> {
    const {
      method = "GET",
      headers = {},
      body,
      requiresAuth = true,
      timeout = this.timeout,
      baseUrl = this.baseUrl, // Use custom baseUrl or default
    } = config;

    const url = endpoint.startsWith("http")
      ? endpoint
      : `${baseUrl}${endpoint}`;

    const requestHeaders: Record<string, string> = {
      "X-App-Source": "react",
      ...headers,
    };

    // Only set Content-Type for JSON requests (not FormData)
    // FormData will set its own Content-Type with boundary
    if (!(body instanceof FormData)) {
      requestHeaders["Content-Type"] = "application/json";
    }

    // Inject authorization token
    if (requiresAuth) {
      const token = TokenManager.getAccessToken();
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body:
          body instanceof FormData
            ? body
            : body
              ? JSON.stringify(body)
              : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 - try to refresh token and retry
      if (response.status === 401 && requiresAuth) {
        const refreshed = await this.handleUnauthorized();
        if (refreshed) {
          // Retry request with new token
          return this.request<T>(endpoint, config);
        }
        // If refresh failed, throw error to trigger re-login
        throw new ApiError(401, "AUTH_REQUIRED", "Please log in again");
      }

      return await ErrorHandler.handleResponse(response);
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        throw new ApiError(
          408,
          "TIMEOUT",
          "Request timed out. Please try again.",
        );
      }

      if (error instanceof ApiError) {
        ErrorHandler.logError(error, `${method} ${endpoint}`);

        // Retry on recoverable errors (but NOT for DELETE operations)
        if (
          method !== "DELETE" &&
          ErrorHandler.isRecoverable(error) &&
          this.maxRetries > 0
        ) {
          this.maxRetries--;
          await this.delay(1000); // Wait 1 second before retry
          return this.request<T>(endpoint, config);
        }

        throw error;
      }

      // Network error or other unexpected error
      throw new ApiError(
        0,
        "NETWORK_ERROR",
        "Network error. Please check your connection.",
        error,
      );
    } finally {
      this.maxRetries = 2; // Reset retry counter
    }
  }

  /**
   * Handle 401 unauthorized - attempt token refresh
   */
  private async handleUnauthorized(): Promise<boolean> {
    try {
      const newToken = await TokenManager.refreshAccessToken();
      return !!newToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    }
  }

  /**
   * Delay utility for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Convenience methods
   */
  async get<T = any>(
    endpoint: string,
    config?: Omit<RequestConfig, "method">,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "GET" });
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    config?: Omit<RequestConfig, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "POST", body });
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    config?: Omit<RequestConfig, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "PUT", body });
  }

  async delete<T = any>(
    endpoint: string,
    config?: Omit<RequestConfig, "method">,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "DELETE" });
  }

  async patch<T = any>(
    endpoint: string,
    body?: any,
    config?: Omit<RequestConfig, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "PATCH", body });
  }
}

export default ApiClient.getInstance();
